import { ClickEventHandler } from '../modules/dom/_ClickEvent';
import { StyleController } from '../modules/dom/_CssProperty';

/**
 * // Class Obj: モーダルの portal 化（DOM退避・復帰）を管理
 */
class ModalPortal
{
  private readonly mPlaceholderMap: WeakMap<HTMLElement, Comment> = new WeakMap();
  private readonly mParentMap: WeakMap<HTMLElement, Node> = new WeakMap();
  private readonly mOwnerItemModalMap: WeakMap<HTMLElement, HTMLElement> = new WeakMap();

  public registerOwner(modal: HTMLElement): void
  {
    if (this.mOwnerItemModalMap.has(modal))
    {
      return;
    }

    const owner_item_modal = modal.closest<HTMLElement>('.item-modal');
    if (owner_item_modal)
    {
      this.mOwnerItemModalMap.set(modal, owner_item_modal);
    }
  }

  public getOwnerItemModal(modal: HTMLElement): HTMLElement | null
  {
    return this.mOwnerItemModalMap.get(modal) ?? null;
  }

  public isPortaled(modal: HTMLElement): boolean
  {
    return this.mPlaceholderMap.has(modal);
  }

  public portal(modal: HTMLElement): void
  {
    if (this.isPortaled(modal))
    {
      return;
    }

    this.registerOwner(modal);

    const parent = modal.parentNode;
    if (!parent)
    {
      return;
    }

    const placeholder = document.createComment('modal-origin');
    parent.insertBefore(placeholder, modal);

    this.mParentMap.set(modal, parent);
    this.mPlaceholderMap.set(modal, placeholder);

    document.body.appendChild(modal);
  }

  public restore(modal: HTMLElement): void
  {
    const placeholder = this.mPlaceholderMap.get(modal);
    const parent = this.mParentMap.get(modal);

    if (!placeholder || !parent)
    {
      return;
    }

    placeholder.parentNode?.insertBefore(modal, placeholder);
    placeholder.remove();

    this.mPlaceholderMap.delete(modal);
    this.mParentMap.delete(modal);
  }

  public restoreAll(modals: HTMLElement[]): void
  {
    modals.forEach((modal: HTMLElement) =>
    {
      this.restore(modal);
    });
  }
}

/**
 * // Class Obj: モーダルウィンドウのメインとなる処理を管理
 * イベント登録はここに集約（ModalState 側で add/remove しない）
 */
export class ModalDisplay
{
  private readonly mClickEventHandler: ClickEventHandler;
  private readonly mModalPortal: ModalPortal;
  private readonly mModalAttachment: ModalAttachment;
  private readonly mModalOperation: ModalOperation;
  private readonly mModalState: ModalState;

  constructor(site_body: HTMLElement, target_modals: HTMLElement | null)
  {
    this.mClickEventHandler = new ClickEventHandler();

    this.mModalPortal = new ModalPortal();
    this.mModalAttachment = new ModalAttachment(site_body);
    this.mModalOperation = new ModalOperation(target_modals, this.mModalPortal);
    this.mModalState = new ModalState(this.mModalAttachment, this.mModalOperation, this.mModalPortal);
  }

  public runProgram(): void
  {
    this.bindOpen(this.mModalOperation.mOpenButtons);
    this.bindClose(this.mModalOperation.mCloseButtons);
    this.bindOverlayClose(this.mModalAttachment.mOverlay);

    this.bindSwitch(this.mModalOperation.mPrevButtons);
    this.bindSwitch(this.mModalOperation.mNextButtons);

    this.bindSelectItem(this.mModalOperation.mChooseListButtons);
  }

  private bindOpen(trigger_elements: HTMLCollectionOf<HTMLElement>): void
  {
    this.mClickEventHandler.addEventToElements(
      (element: HTMLElement) =>
      {
        this.mModalState.openByTrigger(element);

        // 既存仕様：open トリガーからメディア抽出するケースがあるなら残す
        this.mModalOperation.changeWindowItemByElement(element);
      },
      trigger_elements,
    );
  }

  private bindClose(trigger_elements: HTMLCollectionOf<HTMLElement>): void
  {
    this.mClickEventHandler.addEventToElements(
      (_element: HTMLElement) =>
      {
        // close は「全閉じ」に統一（data-target 依存を排除）
        this.mModalState.closeAll();
      },
      trigger_elements,
    );
  }

  private bindOverlayClose(target_element: HTMLElement | null): void
  {
    if (!target_element)
    {
      return;
    }

    target_element.addEventListener('click', () =>
    {
      this.mModalState.closeAll();
    });
  }

  private bindSwitch(trigger_elements: HTMLCollectionOf<HTMLElement>): void
  {
    this.mClickEventHandler.addEventToElements(
      (element: HTMLElement) =>
      {
        // next/prev は状態を変えない（openのまま）
        this.mModalOperation.changeWindowByElement(element);
      },
      trigger_elements,
    );
  }

  private bindSelectItem(trigger_elements: HTMLCollectionOf<HTMLElement>): void
  {
    this.mClickEventHandler.addEventToElements(
      (element: HTMLElement) =>
      {
        this.mModalOperation.changeWindowItemByElement(element);
      },
      trigger_elements,
    );
  }
}

/**
 * // Class Obj: モーダルウィンドウの操作に関するパーツを管理
 */
class ModalOperation
{
  private readonly mStyleController: StyleController;
  private readonly mModalWindowItem: ModalWindowItem;
  private readonly mModalPortal: ModalPortal;

  public readonly mModals: HTMLElement | null;

  private readonly mAllModals: HTMLElement[];

  public readonly mOpenButtons: HTMLCollectionOf<HTMLElement>;
  public readonly mCloseButtons: HTMLCollectionOf<HTMLElement>;
  public readonly mNextButtons: HTMLCollectionOf<HTMLElement>;
  public readonly mPrevButtons: HTMLCollectionOf<HTMLElement>;
  public readonly mPagerButtons: HTMLCollectionOf<HTMLElement>;
  public readonly mChooseListButtons: HTMLCollectionOf<HTMLElement>;

  constructor(target_modals_id: HTMLElement | null, modal_portal: ModalPortal)
  {
    this.mStyleController = new StyleController();
    this.mModalWindowItem = new ModalWindowItem();
    this.mModalPortal = modal_portal;

    this.mModals = target_modals_id;

    this.mOpenButtons = this.mModals?.getElementsByClassName('modal_open') as HTMLCollectionOf<HTMLElement>;
    this.mCloseButtons = this.mModals?.getElementsByClassName('modal_close') as HTMLCollectionOf<HTMLElement>;
    this.mPrevButtons = this.mModals?.getElementsByClassName('modal_prev') as HTMLCollectionOf<HTMLElement>;
    this.mNextButtons = this.mModals?.getElementsByClassName('modal_next') as HTMLCollectionOf<HTMLElement>;
    this.mPagerButtons = this.mModals?.getElementsByClassName('modal_pager') as HTMLCollectionOf<HTMLElement>;
    this.mChooseListButtons = this.mModals?.getElementsByClassName('modal_choose_item') as HTMLCollectionOf<HTMLElement>;

    const modals = this.mModals?.getElementsByClassName('modal') as HTMLCollectionOf<HTMLElement> | undefined;
    this.mAllModals = modals ? Array.from(modals) : [];

    this.mAllModals.forEach((modal: HTMLElement) =>
    {
      this.mModalPortal.registerOwner(modal);
    });
  }

  public getAllModals(): HTMLElement[]
  {
    return this.mAllModals;
  }

  public hideAllModals(): void
  {
    this.mAllModals.forEach((modal: HTMLElement) =>
    {
      this.mStyleController.applyDisplayToElement(modal.style, 'none');
    });
  }

  public changeWindowByElement(element: HTMLElement): void
  {
    const current_modal: HTMLElement | null = element.closest<HTMLElement>('.modal');
    const target_modal_id: string | null = element.getAttribute('data-target');

    const target_modal: HTMLElement | null =
      target_modal_id
        ? document.getElementById(target_modal_id)
        : null;

    if (target_modal)
    {
      this.mModalPortal.portal(target_modal);
    }

    if (current_modal)
    {
      this.mStyleController.applyDisplayToElement(current_modal.style, 'none');
    }

    if (target_modal)
    {
      this.mStyleController.applyDisplayToElement(target_modal.style, 'block');
    }

    this.mAllModals.forEach((modal: HTMLElement) =>
    {
      if (modal !== current_modal && modal !== target_modal)
      {
        this.mStyleController.applyDisplayToElement(modal.style, 'none');
      }
    });
  }

  public changeWindowItemByElement(element: HTMLElement): void
  {
    const DISP_WIDTH = 480;
    const DISP_HEIGHT = 720;

    const img: HTMLImageElement | null = element.querySelector('img');
    const source: HTMLSourceElement | null = element.querySelector('source');

    let current_item_modal: HTMLElement | null = element.closest<HTMLElement>('.item-modal');

    if (!current_item_modal)
    {
      const current_modal: HTMLElement | null = element.closest<HTMLElement>('.modal');
      if (current_modal)
      {
        current_item_modal = this.mModalPortal.getOwnerItemModal(current_modal);
      }
    }

    const disp_media: HTMLElement | null | undefined = current_item_modal?.querySelector('.disp-main .media');

    let src: string | null = null;
    let attrs: Record<string, any> = {};

    if (img)
    {
      src = img.src;
      attrs = {
        srcset: img.srcset,
        sizes: img.sizes,
        width: DISP_WIDTH,
        height: DISP_HEIGHT,
        alt: img.alt || '',
      };
    }
    else if (source)
    {
      src = source.src;
      attrs = {
        autoplay: true,
        muted: true,
        loop: true,
        playsInline: true,
        controls: true,
        controlsList: 'nodownload',
      };
    }

    if (src)
    {
      const create_element: HTMLElement | null = this.mModalWindowItem.createMediaElement(src, attrs);

      if (create_element && disp_media)
      {
        disp_media.innerHTML = '';
        disp_media.appendChild(create_element);
      }
      else
      {
        console.error('メディア要素の作成に失敗しました。');
      }
    }
  }
}

class ModalWindowItem
{
  private getFileType(src: string): string | null
  {
    const match: RegExpMatchArray | null = src.match(/\.(\w+)(?:\?.*)?$/);
    return match ? match[1].toLowerCase() : null;
  }

  private getMediaType(file_type: string): 'image' | 'video' | null
  {
    const media_types = {
      image: ['jpg', 'jpeg', 'png', 'webp'],
      video: ['mp4', 'webm'],
    };

    for (const [type, extensions] of Object.entries(media_types))
    {
      if (extensions.includes(file_type))
      {
        return type as 'image' | 'video';
      }
    }

    return null;
  }

  public createMediaElement(src: string, attrs: Record<string, any>): HTMLElement | null
  {
    const file_type: string | null = this.getFileType(src);

    if (!file_type)
    {
      console.error('ファイルタイプを判定できません。');
      return null;
    }

    const media_type: 'image' | 'video' | null = this.getMediaType(file_type);

    if (!media_type)
    {
      console.warn('ファイルタイプが適正（imageまたはvideo）ではありません。');
      return null;
    }

    if (media_type === 'image')
    {
      const img: HTMLImageElement = document.createElement('img');
      img.classList.add('image');
      Object.assign(img, attrs, { src });
      return img;
    }

    if (media_type === 'video')
    {
      const video: HTMLVideoElement = document.createElement('video');
      video.classList.add('video');

      const source: HTMLSourceElement = document.createElement('source');
      Object.assign(source, { src, type: `video/${file_type}` });

      Object.assign(video, attrs);
      video.appendChild(source);
      return video;
    }

    return null;
  }
}

/**
 * // Class Obj: モーダルウィンドウに付属するパーツ（オーバーレイ等）を管理
 */
class ModalAttachment
{
  private readonly mStyleController: StyleController;
  private readonly mSiteBody: HTMLElement;
  public readonly mOverlay: HTMLElement;

  constructor(site_body: HTMLElement)
  {
    this.mStyleController = new StyleController();
    this.mSiteBody = site_body;

    this.mOverlay = this.ensureOverlay();
  }

  private ensureOverlay(): HTMLElement
  {
    let overlay = document.getElementById('modalOverlay') as HTMLElement | null;

    if (!overlay)
    {
      overlay = document.createElement('div');
      overlay.id = 'modalOverlay';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.style.display = 'none';

      document.body.appendChild(overlay);
    }

    return overlay;
  }

  public toggleOverlay(show: boolean): void
  {
    this.mStyleController.applyDisplayToElement(this.mOverlay.style, show ? 'block' : 'none');
    this.mOverlay.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  public toggleScrollBody(enable: boolean): void
  {
    this.mSiteBody.classList.toggle('no_scroll', !enable);
  }
}

/**
 * // Class Obj: モーダルウィンドウの状態（表示、非表示）を管理
 * イベントの add/remove はしない（状態遷移のみ）
 */
class ModalState
{
  private readonly mModalAttachment: ModalAttachment;
  private readonly mModalOperation: ModalOperation;
  private readonly mModalPortal: ModalPortal;

  private mIsWindowOpen: boolean = false;
  private mLastTrigger: HTMLElement | null = null;

  constructor(modal_attachment: ModalAttachment, modal_operation: ModalOperation, modal_portal: ModalPortal)
  {
    this.mModalAttachment = modal_attachment;
    this.mModalOperation = modal_operation;
    this.mModalPortal = modal_portal;
  }

  public openByTrigger(trigger: HTMLElement): void
  {
    const target_modal_id: string | null = trigger.getAttribute('data-target');
    if (!target_modal_id)
    {
      return;
    }

    const target_modal = document.getElementById(target_modal_id) as HTMLElement | null;
    if (!target_modal)
    {
      return;
    }

    this.mLastTrigger = trigger;

    this.mModalPortal.portal(target_modal);
    this.mModalOperation.changeWindowByElement(trigger);

    this.mModalAttachment.toggleOverlay(true);
    this.mModalAttachment.toggleScrollBody(false);

    this.mIsWindowOpen = true;
  }

  public closeAll(): void
  {
    if (!this.mIsWindowOpen)
    {
      return;
    }

    this.mModalOperation.hideAllModals();

    this.mModalAttachment.toggleOverlay(false);
    this.mModalAttachment.toggleScrollBody(true);

    this.mModalPortal.restoreAll(this.mModalOperation.getAllModals());

    this.mIsWindowOpen = false;

    // 任意：開いたボタンへフォーカスを戻す（キーボード操作の自然さが上がる）
    this.mLastTrigger?.focus();
    this.mLastTrigger = null;
  }
}
